import { supabase } from '../lib/supabase';
import { LEGAL_VERSIONS } from '../config/legalVersions';
import secureLog from '../utils/secureLogging';

const LEGAL_TABLE = 'legal_acceptances_t7k9m2n';

export class LegalService {
  
  /**
   * Test database connection and table access
   */
  static async testConnection() {
    try {
      secureLog.debug('LegalService: Testing database connection...');
      
      // Test basic table access - just try to select from the table
      const { data, error } = await supabase
        .from(LEGAL_TABLE)
        .select('id')
        .limit(1);

      if (error) {
        console.error('LegalService: Connection test failed:', error);
        
        if (error.code === '42P01') {
          return { 
            success: false, 
            error: `Table '${LEGAL_TABLE}' does not exist. Please run the legal acceptances migration.`,
            code: error.code
          };
        }
        
        if (error.code === '42501') {
          return { 
            success: false, 
            error: 'Permission denied. Check RLS policies and user authentication.',
            code: error.code
          };
        }
        
        return { success: false, error: error.message, code: error.code };
      }

      // Test user authentication
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError || !user) {
        return { 
          success: false, 
          error: 'User not authenticated with Supabase',
          authError: authError?.message 
        };
      }

      secureLog.debug('LegalService: Connection test successful');
      return { 
        success: true, 
        data,
        userId: user.id,
        userEmail: user.email 
      };

    } catch (error) {
      console.error('LegalService: Connection test error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Check if user has accepted current versions of legal documents
   */
  static async checkLegalAcceptance(userId) {
    try {
      if (!userId) {
        console.warn('LegalService: No user ID provided');
        return { needsAcceptance: true, missingDocuments: ['terms', 'privacy', 'cookies'] };
      }

      secureLog.debug('LegalService: Checking legal acceptance for user:', userId);
      secureLog.debug('LegalService: Current legal versions:', LEGAL_VERSIONS);

      // Test connection first
      const connectionTest = await this.testConnection();
      if (!connectionTest.success) {
        console.error('LegalService: Database connection failed:', connectionTest.error);
        return { 
          needsAcceptance: true, 
          missingDocuments: ['terms', 'privacy', 'cookies'],
          error: connectionTest.error
        };
      }

      secureLog.debug('LegalService: Database connection successful, checking user acceptance...');

      const { data, error } = await supabase
        .from(LEGAL_TABLE)
        .select('*')
        .eq('user_id', userId)
        .order('accepted_at', { ascending: false })
        .limit(1)
        .maybeSingle(); // Use maybeSingle to avoid errors when no data found

      if (error) {
        console.error('LegalService: Error checking acceptance:', error);
        return { 
          needsAcceptance: true, 
          missingDocuments: ['terms', 'privacy', 'cookies'],
          error: error.message 
        };
      }

      // No acceptance record found
      if (!data) {
        secureLog.debug('LegalService: No legal acceptance record found - user needs to accept all documents');
        return { needsAcceptance: true, missingDocuments: ['terms', 'privacy', 'cookies'] };
      }

      secureLog.debug('LegalService: Found acceptance record:', data);

      // Check if current versions match
      const missingDocuments = [];
      
      if (data.terms_version !== LEGAL_VERSIONS.TERMS) {
        secureLog.debug(`LegalService: Terms version mismatch - user: ${data.terms_version}, current: ${LEGAL_VERSIONS.TERMS}`);
        missingDocuments.push('terms');
      }
      
      if (data.privacy_version !== LEGAL_VERSIONS.PRIVACY) {
        secureLog.debug(`LegalService: Privacy version mismatch - user: ${data.privacy_version}, current: ${LEGAL_VERSIONS.PRIVACY}`);
        missingDocuments.push('privacy');
      }
      
      if (data.cookies_version !== LEGAL_VERSIONS.COOKIES) {
        console.log(`LegalService: Cookies version mismatch - user: ${data.cookies_version}, current: ${LEGAL_VERSIONS.COOKIES}`);
        missingDocuments.push('cookies');
      }

      const needsAcceptance = missingDocuments.length > 0;
      

      return {
        needsAcceptance,
        missingDocuments,
        lastAcceptance: data,
        currentVersions: LEGAL_VERSIONS
      };

    } catch (error) {
      console.error('LegalService: Unexpected error checking acceptance:', error);
      return { 
        needsAcceptance: true, 
        missingDocuments: ['terms', 'privacy', 'cookies'],
        error: error.message 
      };
    }
  }

  /**
   * Record user acceptance of legal documents (enhanced with better error handling)
   */
  static async recordAcceptance(userId, acceptedDocuments = {}) {
    try {
      if (!userId) {
        throw new Error('User ID is required');
      }

      // Validate userId format (should be a UUID)
      if (typeof userId !== 'string' || !userId.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
        throw new Error(`Invalid user ID format: ${userId}. Expected UUID format.`);
      }

      console.log('LegalService: Recording acceptance for user:', userId);
      console.log('LegalService: Accepted documents:', acceptedDocuments);
      console.log('LegalService: Current versions:', LEGAL_VERSIONS);

      // Test connection first (but don't fail if it's just an RLS permission issue)
      const connectionTest = await this.testConnection();
      if (!connectionTest.success) {
        // If it's a permission error (42501), we'll still try the insert
        // because RLS might allow INSERT even if SELECT is blocked
        if (connectionTest.code === '42501') {
          console.warn('LegalService: Connection test failed due to RLS, but will attempt insert anyway');
        } else {
          // For other errors (like table doesn't exist), fail immediately
          throw new Error(`Database connection failed: ${connectionTest.error}`);
        }
      }

      // Prepare acceptance data
      const acceptanceData = {
        user_id: userId,
        terms_version: acceptedDocuments.terms ? LEGAL_VERSIONS.TERMS : '',
        privacy_version: acceptedDocuments.privacy ? LEGAL_VERSIONS.PRIVACY : '',
        cookies_version: acceptedDocuments.cookies ? LEGAL_VERSIONS.COOKIES : '',
        accepted_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      console.log('LegalService: Inserting acceptance data:', acceptanceData);

      // Insert a new acceptance record (each acceptance is tracked separately for audit trail)
      const { data, error } = await supabase
        .from(LEGAL_TABLE)
        .insert(acceptanceData)
        .select()
        .single();

      if (error) {
        console.error('LegalService: Error upserting acceptance:', error);
        
        // Provide more specific error messages
        if (error.code === '23503') {
          throw new Error(`User ID ${userId} does not exist in auth.users table. Please ensure the user is properly authenticated.`);
        }
        
        if (error.code === '42501') {
          throw new Error('Permission denied. Please check RLS policies and ensure user is authenticated.');
        }
        
        throw new Error(`Failed to record legal acceptance: ${error.message} (Code: ${error.code})`);
      }

      if (!data) {
        throw new Error('No data returned from upsert operation');
      }

      console.log('LegalService: Acceptance recorded successfully:', data);
      
      // Dispatch event for other components
      window.dispatchEvent(new CustomEvent('legalAcceptanceRecorded', {
        detail: { userId, acceptedDocuments, data }
      }));

      return data;

    } catch (error) {
      console.error('LegalService: Error recording acceptance:', error);
      throw error;
    }
  }

  /**
   * Get user's legal acceptance history (for admin/audit purposes)
   */
  static async getAcceptanceHistory(userId) {
    try {
      const { data, error } = await supabase
        .from(LEGAL_TABLE)
        .select('*')
        .eq('user_id', userId)
        .order('accepted_at', { ascending: false });

      if (error) {
        console.error('LegalService: Error fetching acceptance history:', error);
        throw error;
      }

      return data || [];

    } catch (error) {
      console.error('LegalService: Error fetching acceptance history:', error);
      throw error;
    }
  }

  /**
   * Check if user needs to accept legal documents (convenience method)
   */
  static async needsLegalAcceptance(userId) {
    const result = await this.checkLegalAcceptance(userId);
    return result.needsAcceptance;
  }

  /**
   * Debug helper to check user's current legal status
   */
  static async debugUserLegalStatus(userId) {
    try {
      console.log('LegalService: DEBUG - Checking legal status for user:', userId);
      
      // Test connection
      const connectionTest = await this.testConnection();
      console.log('LegalService: DEBUG - Connection test:', connectionTest);
      
      if (!connectionTest.success) {
        return {
          error: 'Database connection failed',
          connectionTest,
          userId
        };
      }

      // Get user's acceptance record
      const { data, error } = await supabase
        .from(LEGAL_TABLE)
        .select('*')
        .eq('user_id', userId);

      console.log('LegalService: DEBUG - Query result:', { data, error });

      // Get current user from Supabase auth
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      console.log('LegalService: DEBUG - Current auth user:', { user: user?.id, authError });

      return {
        userId,
        acceptanceRecords: data,
        queryError: error,
        currentAuthUser: user?.id,
        authError,
        connectionTest,
        currentVersions: LEGAL_VERSIONS,
        tableExists: connectionTest.success
      };

    } catch (error) {
      console.error('LegalService: DEBUG - Error:', error);
      return {
        userId,
        error: error.message,
        stack: error.stack
      };
    }
  }
}

// Cookie consent management (unchanged but enhanced)
export class CookieService {
  
  static STORAGE_KEY = 'trackio_cookie_consent';
  
  static getConsent() {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.warn('CookieService: Error reading consent:', error);
    }
    
    return null;
  }

  static setConsent(preferences) {
    try {
      const consentData = {
        ...preferences,
        timestamp: Date.now(),
        version: LEGAL_VERSIONS.COOKIES
      };
      
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(consentData));
      
      window.dispatchEvent(new CustomEvent('cookieConsentUpdated', {
        detail: consentData
      }));
      
      console.log('CookieService: Consent preferences saved:', consentData);
      return consentData;
      
    } catch (error) {
      console.error('CookieService: Error saving consent:', error);
      throw error;
    }
  }

  static hasConsent(category) {
    const consent = this.getConsent();
    if (!consent) return false;
    
    if (category === 'essential') return true;
    
    return consent[category] === true;
  }

  static needsConsent() {
    const consent = this.getConsent();
    
    if (!consent) return true;
    
    if (consent.version !== LEGAL_VERSIONS.COOKIES) return true;
    
    return false;
  }

  static clearConsent() {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
      window.dispatchEvent(new CustomEvent('cookieConsentCleared'));
    } catch (error) {
      console.warn('CookieService: Error clearing consent:', error);
    }
  }
}